import React, { useState, useEffect, useRef } from "react";
import { FaBars, FaCheck, FaMinus, FaEdit, FaEye } from "react-icons/fa";
import { RiDeleteBin6Line } from "react-icons/ri";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PageHeading from "../PageHeading";
import { Link } from "react-router-dom";
import AddBadStockModal from "./AddBadStockModal";
import AddMedicineModal from "../AddMedicalModal";
import { FaDownload, FaPlus } from "react-icons/fa6";
import axios from "axios";
import EditMedicineModal from "../EditMedicineModal";
import ExportOptions from "../ExportOptions";
import TableSkeleton from "../TableSkeleton";
import ConfirmActionModal from "../ConfirmDeleteModal";
import GenerateBillModal from "./GenerateBillModal";
import MedicineDetailsModal from "./MedicineDetailsModal";
import Pagination from "../Shared/Pagination";

const MEDICINE_API = "https://api.muktihospital.com/api/medicine";
const MEDICINE_GROUP_API = "https://api.muktihospital.com/api/medicine-group";
const MEDICINE_CATEGORY_API =
  "https://api.muktihospital.com/api/pharmacy-category";
const MEDICINE_COMPANY_API =
  "https://api.muktihospital.com/api/medicine-company";
const PURCHASE_API = "https://api.muktihospital.com/api/medicine-purchase";
const MEDICINE_UNIT_API = "https://api.muktihospital.com/api/pharmacy-unit";
const API_KEY =
  "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";

const Medicines = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
  const [generateBillModalOpen, setGenerateBillModalOpen] = useState(false);
  const [addBadStockModalOpen, setAddBadStockModalOpen] = useState(false);
  const [addMedicineModalOpen, setAddMedicineModalOpen] = useState(false);
  const [editMedicineModalOpen, setEditMedicineModalOpen] = useState(false);
  const [medicineDetailsModalOpen, setMedicineDetailsModalOpen] =
    useState(false);
  const [selectedMedicine, setSelectedMedicine] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [medicines, setMedicines] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [categories, setCategories] = useState([]);
  const [groups, setGroups] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [units, setUnits] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const exportDropdownRef = useRef(null);
  const [totalRecords, setTotalRecords] = useState(0);
  const itemsPerPage = 10;

  const skeletonColumns = [
    { width: 20, height: 16, className: "text-left", skeletonClassName: "ml-3 mt-2" },
    { width: 60, height: 16, className: "text-left" }, // SL No
    { width: 150, height: 16, className: "text-left" }, // Medicine Name
    { width: 150, height: 16, className: "text-left" }, // Medicine Company
    { width: 150, height: 16, className: "text-left" }, // Medicine Category
    { width: 120, height: 16, className: "text-left" }, // Medicine Group
    { width: 120, height: 16, className: "text-left" }, // Unit
    { width: 80, height: 16, className: "text-left" }, // Available Qty
    { width: 120, height: 16, className: "text-left" }, // Actions
  ];

  const toggleExportDropdown = () => setExportDropdownOpen((prev) => !prev);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        exportDropdownRef.current &&
        !exportDropdownRef.current.contains(event.target)
      ) {
        setExportDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (
      generateBillModalOpen ||
      addBadStockModalOpen ||
      addMedicineModalOpen ||
      editMedicineModalOpen ||
      medicineDetailsModalOpen ||
      deleteModalOpen
    ) {
      setExportDropdownOpen(false);
    }
  }, [
    generateBillModalOpen,
    addBadStockModalOpen,
    addMedicineModalOpen,
    editMedicineModalOpen,
    medicineDetailsModalOpen,
    deleteModalOpen,
  ]);

  useEffect(() => {
    return () => setExportDropdownOpen(false);
  }, []);

  const fetchData = async (page = currentPage) => {
    setIsLoading(true);
    setError(null);
    try {
      const [
        medicineRes,
        categoryRes,
        groupRes,
        companyRes,
        unitRes,
        purchaseRes,
      ] = await Promise.all([
        axios.get(`${MEDICINE_API}?page=${page}&limit=${itemsPerPage}`, {
          headers: { "x-api-key": API_KEY },
        }),
        axios.get(MEDICINE_CATEGORY_API, { headers: { "x-api-key": API_KEY } }),
        axios.get(MEDICINE_GROUP_API, { headers: { "x-api-key": API_KEY } }),
        axios.get(MEDICINE_COMPANY_API, { headers: { "x-api-key": API_KEY } }),
        axios.get(MEDICINE_UNIT_API, { headers: { "x-api-key": API_KEY } }),
        // Ensure backend sends items array in /all route
        axios.get(`${PURCHASE_API}/all`, { headers: { "x-api-key": API_KEY } }),
      ]);

      const purchaseArray = Array.isArray(purchaseRes.data.purchases)
        ? [...purchaseRes.data.purchases]
        : [];
      setPurchases(purchaseArray);

      // 🟢 1. ROBUST QUANTITY CALCULATION
      const processedMedicines = medicineRes.data.medicines.map((medicine) => {
        const totalQuantity = purchaseArray.reduce((sum, purchase) => {
          
          // Handle both Relation (Array) and Legacy (String) items
          let items = [];
          if (Array.isArray(purchase.items)) {
            items = purchase.items;
          } else if (typeof purchase.items === 'string') {
            try {
              items = JSON.parse(purchase.items);
            } catch (e) {
              items = [];
            }
          }

          const itemQuantity = items.reduce((itemSum, item) => {
            // Check matching ID or Name
            const isIdMatch = parseInt(item.medicineId) === parseInt(medicine.id);
            const isNameMatch = item.medicineName?.toLowerCase().trim() === medicine.medicineName?.toLowerCase().trim();

            if (isIdMatch || isNameMatch) {
              return itemSum + (parseInt(item.availableQuantity) || 0);
            }
            return itemSum;
          }, 0);

          return sum + itemQuantity;
        }, 0);

        return {
          ...medicine,
          availableQty: totalQuantity,
        };
      });

      setMedicines(Array.isArray(processedMedicines) ? [...processedMedicines] : []);
      setTotalPages(medicineRes.data.pagination?.totalPages || 1);
      setTotalRecords(medicineRes.data.pagination?.totalItems || processedMedicines.length);
      // console.log("DEBUG: fetchData response", {
      //   medicines: processedMedicines,
      //   totalPages: medicineRes.data.pagination?.totalPages,
      //   totalRecords: medicineRes.data.pagination?.totalItems
      // });

      // If no medicines and not on page 1, try previous page
      if (processedMedicines.length === 0 && page > 1) {
        // console.log(`No data on page ${page}, trying page ${page - 1}`);
        await fetchData(page - 1);
      }

      setCategories(
        Array.isArray(categoryRes.data.categories)
          ? [...categoryRes.data.categories]
          : Array.isArray(categoryRes.data)
          ? [...categoryRes.data]
          : []
      );

      setGroups(
        Array.isArray(groupRes.data.doses)
          ? [...groupRes.data.doses]
          : Array.isArray(groupRes.data.items)
          ? [...groupRes.data.items]
          : Array.isArray(groupRes.data)
          ? [...groupRes.data]
          : []
      );

      setCompanies(
        Array.isArray(companyRes.data.categories)
          ? [...companyRes.data.categories]
          : Array.isArray(companyRes.data)
          ? [...companyRes.data]
          : []
      );

      setUnits(
        Array.isArray(unitRes.data.categories)
          ? [...unitRes.data.categories]
          : Array.isArray(unitRes.data)
          ? [...unitRes.data]
          : []
      );
    } catch (error) {
      console.error("DEBUG: fetchData error:", error);
      setError(
        error.response?.data?.error || "Failed to fetch data from the API"
      );
      toast.error(
        error.response?.data?.error ||
          "Failed to fetch data. Please check your network or API key."
      );
      setMedicines([]);
      setPurchases([]);
      setCategories([]);
      setGroups([]);
      setCompanies([]);
      setUnits([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, refreshTrigger]);

  // Ensure currentPage doesn't exceed totalPages
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      // console.log(`Adjusting page from ${currentPage} to ${totalPages}`);
      setCurrentPage(totalPages);
    } else if (totalPages === 0) {
      // console.log("No data, resetting to page 1");
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  const filteredMedicines = medicines.filter(
    (medicine) =>
      medicine &&
      (medicine.medicineName
        ?.toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
        medicine.medicineCompany
          ?.toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        medicine.medicineCategory
          ?.toLowerCase()
          .includes(searchQuery.toLowerCase()))
  );

  const currentData = filteredMedicines;

  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages) {
      // console.log(`Changing page to ${page}`);
      setCurrentPage(page);
      setExportDropdownOpen(false);
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedRows(currentData.map((medicine) => medicine.id));
    } else {
      setSelectedRows([]);
    }
    setExportDropdownOpen(false);
  };

  const handleRowSelect = (medicineId) => {
    setSelectedRows((prevSelectedRows) =>
      prevSelectedRows.includes(medicineId)
        ? prevSelectedRows.filter((id) => id !== medicineId)
        : [...prevSelectedRows, medicineId]
    );
    setExportDropdownOpen(false);
  };

  const handleDelete = async (medicineIds, isFromModal = false) => {
    const ids = Array.isArray(medicineIds) ? medicineIds : [medicineIds];
    if (!ids.length) {
      toast.error("No medicines selected", { autoClose: 2000 });
      return;
    }

    setIsLoading(true);
    try {
      if (!isFromModal) {
        await Promise.all(
          ids.map((id) =>
            axios.delete(`${MEDICINE_API}/delete/${id}`, {
              headers: { "x-api-key": API_KEY },
            })
          )
        );
      }

      // Optimistically update state
      const updatedMedicines = medicines.filter(
        (medicine) => !ids.includes(String(medicine.id))
      );
      const updatedFilteredMedicines = filteredMedicines.filter(
        (medicine) => !ids.includes(String(medicine.id))
      );
      const newTotalPages = Math.ceil(updatedMedicines.length / itemsPerPage) || 1;
      let newPage = currentPage;
      if (currentPage > newTotalPages && newTotalPages > 0) {
        newPage = newTotalPages;
      } else if (updatedFilteredMedicines.length === 0 && newTotalPages > 0) {
        newPage = Math.max(1, currentPage - 1);
      } else if (newTotalPages === 0) {
        newPage = 1;
      }

      setMedicines([...updatedMedicines]);
      setTotalPages(newTotalPages);
      setCurrentPage(newPage);
      setSelectedRows([]);
      toast.success(`${ids.length} medicine(s) deleted successfully!`, {
        autoClose: 2000,
      });
      // console.log("DEBUG: After deletion", {
      //   updatedMedicines,
      //   newTotalPages,
      //   newPage,
      // });

      // Fetch updated data
      await fetchData(newPage);
    } catch (error) {
      console.error("DEBUG: handleDelete error:", error);
      toast.error(
        "Failed to delete medicines: " +
          (error.response?.data?.error || "Network error"),
        { autoClose: 2000 }
      );
      await fetchData(1); // Reset to page 1 on error
    } finally {
      setIsLoading(false);
      setDeleteModalOpen(false);
      setExportDropdownOpen(false);
    }
  };

  const handleSingleDelete = (medicineId) => {
    setSelectedRows([medicineId]);
    setDeleteModalOpen(true);
    setExportDropdownOpen(false);
  };

  const handleView = (medicine) => {
    setSelectedMedicine(medicine);
    setMedicineDetailsModalOpen(true);
    setExportDropdownOpen(false);
  };

  const handleEdit = (medicine) => {
    // console.log("Passing medicine to EditMedicineModal:", medicine);
    setSelectedMedicine(medicine);
    setEditMedicineModalOpen(true);
    setExportDropdownOpen(false);
  };

  const handleOpenBadStockModal = (medicine) => {
    setSelectedMedicine(medicine);
    setAddBadStockModalOpen(true);
    setExportDropdownOpen(false);
  };

  const handleUpdateMedicine = (id, updatedMedicine) => {
    setMedicines((prev) =>
      prev.map((medicine) =>
        medicine.id === id ? { ...medicine, ...updatedMedicine } : medicine
      )
    );
    toast.success("Medicine updated successfully!", { autoClose: 2000 });
    setRefreshTrigger((prev) => prev + 1);
    setExportDropdownOpen(false);
  };

  const handleAddMedicine = (newMedicine) => {
    if (newMedicine && newMedicine.id) {
      setMedicines((prev) => {
        const updatedMedicines = [...prev, { ...newMedicine, availableQty: 0 }];
        const totalItems = updatedMedicines.length;
        const newTotalPages = Math.ceil(totalItems / itemsPerPage);
        setTotalPages(newTotalPages);
        if (currentPage < newTotalPages) {
          return updatedMedicines;
        }
        return updatedMedicines.slice(
          (currentPage - 1) * itemsPerPage,
          currentPage * itemsPerPage
        );
      });
      toast.success("Medicine added successfully!", { autoClose: 2000 });
    } else {
      toast.error("Failed to add medicine: Invalid data", { autoClose: 2000 });
    }
    setRefreshTrigger((prev) => prev + 1);
    setExportDropdownOpen(false);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
    setExportDropdownOpen(false);
  };

  const handleRefresh = () => {
    setRefreshTrigger((prev) => prev + 1);
    setExportDropdownOpen(false);
  };

  const breadcrumbs = [
    { label: "Dashboard", url: "/" },
    { label: "Pharmacy"},
    { label: "Medicines" },
  ];

  const headers = [
    "SL No",
    "Medicine Name",
    "Medicine Company",
    "Medicine Category",
    "Medicine Group",
    "Unit",
    "Available Qty",
  ];

  const dataMapper = (() => {
  let counter = 0;
  return (item) => {
    counter += 1;
    return [
      counter,
      item.medicineName || "-",
      item.medicineCompany || "-",
      item.medicineCategory || "-",
      item.medicineGroup || "-",
      item.unit || "-",
      item.availableQty || "0",
    ].join("\t");
  };
})();

  return (
    <div>
      <style jsx>{`
        @media print {
          .print-hidden {
            display: none !important;
          }
          .print-wrapper {
            width: 100% !important;
            max-width: 100% !important;
            overflow: visible !important;
          }
          .print-table {
            width: 100% !important;
            min-width: auto !important;
            font-size: 10px !important;
          }
          .print-table th,
          .print-table td {
            padding: 6px !important;
            font-size: 10px !important;
          }
          @page {
            size: A4;
            margin: 10mm;
          }
          body {
            margin: 0;
          }
        }
      `}</style>
      <PageHeading title="Medicines" breadcrumbs={breadcrumbs} />
      <ToastContainer />
      <div className="bg-white shadow-sm rounded-lg">
        <div className="flex flex-wrap gap-3 flex-col sm:flex-row md:items-center justify-between border-b border-dashed border-M-text-color/50 p-4 print-hidden">
          <div className="inline-flex items-center gap-3 shrink-0">
            <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
              Medicine List
            </h2>
            {selectedRows.length > 0 && (
              <button
                onClick={() => {
                  setDeleteModalOpen(true);
                  setExportDropdownOpen(false);
                }}
                className="size-7 bg-M-secondary-color/10 text-M-secondary-color hover:bg-M-secondary-color hover:text-white transition-all duration-300 text-base rounded-md inline-flex items-center justify-center"
                disabled={isLoading}
                title="Delete selected rows"
              >
                <RiDeleteBin6Line />
              </button>
            )}
          </div>
          <div className="flex flex-row items-start flex-wrap md:flex-nowrap sm:items-center justify-start sm:justify-end gap-3">
            <input
              type="text"
              className="max-w-48 w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-M-heading-color shrink-0"
              placeholder="Search by name, company, category"
              value={searchQuery}
              onChange={handleSearchChange}
            />
            <Link
              to="/import-medicines"
              className="flex items-center gap-1 bg-[#73CB64] hover:bg-M-primary-color default-button shrink-0"
            >
              <FaDownload /> Import Medicines
            </Link>
            <button
              onClick={() => {
                setAddMedicineModalOpen(true);
                setExportDropdownOpen(false);
              }}
              className="flex items-center gap-1 bg-[#73CB64] hover:bg-M-primary-color default-button shrink-0"
              disabled={isLoading}
            >
              <FaPlus /> Add Medicine
            </button>
            <div className="flex items-center justify-between w-full gap-3">
              <Link
                to="/purchase-medicine"
                className="flex items-center gap-1 bg-[#73CB64] hover:bg-M-primary-color default-button shrink-0"
              >
                <FaBars /> Purchase
              </Link>
              <div className="relative z-20" ref={exportDropdownRef}>
                <ExportOptions
                  data={currentData}
                  dropdownOpen={exportDropdownOpen}
                  toggleDropdown={toggleExportDropdown}
                  title="Medicines"
                  headers={headers}
                  dataMapper={dataMapper}
                />
              </div>
            </div>
          </div>
        </div>

        <GenerateBillModal
          isOpen={generateBillModalOpen}
          onClose={() => {
            setGenerateBillModalOpen(false);
            setExportDropdownOpen(false);
          }}
        />
        <AddBadStockModal
          isOpen={addBadStockModalOpen}
          onClose={() => {
            setAddBadStockModalOpen(false);
            setExportDropdownOpen(false);
          }}
          medicine={selectedMedicine}
          onBadStockSave={handleUpdateMedicine}
        />
        <AddMedicineModal
          isOpen={addMedicineModalOpen}
          onClose={() => {
            setAddMedicineModalOpen(false);
            setExportDropdownOpen(false);
          }}
          onSave={handleAddMedicine}
          categories={categories}
          groups={groups}
          companies={companies}
          units={units}
        />
        <EditMedicineModal
          isOpen={editMedicineModalOpen}
          onClose={() => {
            setEditMedicineModalOpen(false);
            setExportDropdownOpen(false);
          }}
          onSave={handleUpdateMedicine}
          medicine={selectedMedicine}
          categories={categories}
          groups={groups}
          companies={companies}
          units={units}
        />
        <MedicineDetailsModal
          isOpen={medicineDetailsModalOpen}
          onClose={() => {
            setMedicineDetailsModalOpen(false);
            setExportDropdownOpen(false);
          }}
          medicine={selectedMedicine}
          onDelete={(id) => handleDelete([id], true)}
          onSave={async (id, updatedMedicine) => {
            handleUpdateMedicine(id, updatedMedicine);
            await new Promise((resolve) => setTimeout(resolve, 100));
            setMedicineDetailsModalOpen(false);
            setExportDropdownOpen(false);
          }}
          categories={categories}
          groups={groups}
          companies={companies}
          units={units}
          onRefresh={handleRefresh}
        />
        <ConfirmActionModal
          isOpen={deleteModalOpen}
          onClose={() => {
            setDeleteModalOpen(false);
            setExportDropdownOpen(false);
          }}
          onConfirm={() => handleDelete(selectedRows)}
          ids={selectedRows}
          entityName="medicine"
          title="Confirm Delete"
          message={
            Array.isArray(selectedRows) && selectedRows.length > 1
              ? `Do you really want to delete ${selectedRows.length} medicine(s)?`
              : `Do you really want to delete this medicine?`
          }
          confirmText="Delete"
          cancelText="Cancel"
        />
        <div className="overflow-x-auto print-wrapper">
          {error && <div className="p-4 text-center text-red-600">{error}</div>}
          {isLoading ? (
            <table className="min-w-[1440px] w-full divide-y divide-gray-200 print-table">
              <thead>
                <tr>
                  <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3"></th>
                  <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                    SL No
                  </th>
                  <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                    Medicine Name
                  </th>
                  <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                    Medicine Company
                  </th>
                  <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                    Medicine Category
                  </th>
                  <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                    Medicine Group
                  </th>
                  <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                    Unit
                  </th>
                  <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                    Available Qty
                  </th>
                  <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                <TableSkeleton
                  rowCount={itemsPerPage}
                  columns={skeletonColumns}
                  baseColor="#e5e7eb"
                  highlightColor="#f3f4f6"
                />
              </tbody>
            </table>
          ) : currentData.length === 0 ? (
            <div className="p-4 text-center font-inter text-M-heading-color">
              No medicines found
            </div>
          ) : (
            <table className="min-w-[1440px] w-full divide-y divide-gray-200 print-table">
              <thead>
                <tr className="bg-gray-100">
                  <th className="text-left">
                    <div className="relative left-3 -top-[2px]">
                      <input
                        type="checkbox"
                        id="agreement"
                        className="hidden peer"
                        onChange={handleSelectAll}
                        checked={
                          selectedRows.length === currentData.length &&
                          currentData.length > 0
                        }
                        disabled={isLoading}
                      />
                      <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0">
                        <FaCheck className="text-slate-100" />
                      </span>
                      <label
                        htmlFor="agreement"
                        className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6"
                      ></label>
                    </div>
                  </th>
                  <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                    SL No
                  </th>
                  <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                    Medicine Name
                  </th>
                  <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                    Medicine Company
                  </th>
                  <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                    Medicine Category
                  </th>
                  <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                    Medicine Group
                  </th>
                  <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                    Unit
                  </th>
                  <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                    Available Qty
                  </th>
                  <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {currentData.map((medicine, index) => (
                  <tr key={medicine.id} className="border-b hover:bg-gray-50">
                    <td className="text-left capitalize font-inter font-medium text-sm text-M-heading-color">
                      <div className="relative left-3 -top-[3px]">
                        <input
                          type="checkbox"
                          id={`medicine-${index}`}
                          className="hidden peer"
                          checked={selectedRows.includes(medicine.id)}
                          onChange={() => handleRowSelect(medicine.id)}
                          disabled={isLoading}
                        />
                        <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0">
                          <FaCheck className="text-slate-100" />
                        </span>
                        <label
                          htmlFor={`medicine-${index}`}
                          className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6"
                        ></label>
                      </div>
                    </td>
                    <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                      {(currentPage - 1) * itemsPerPage + index + 1}
                    </td>
                    <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                      {medicine.medicineName || "-"}
                    </td>
                    <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                      {medicine.medicineCompany || "-"}
                    </td>
                    <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                      {medicine.medicineCategory || "-"}
                    </td>
                    <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                      {medicine.medicineGroup || "-"}
                    </td>
                    <td className="text-left font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                      {medicine.unit || "-"}
                    </td>
                    <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                      {medicine.availableQty || "0"}
                    </td>
                    <td className="p-3 text-left space-x-2 flex shrink-0">
                      <button
                        onClick={() => handleView(medicine)}
                        className="size-7 rounded-full bg-M-primary-color/20 text-M-primary-color text-sm inline-flex items-center justify-center hover:bg-M-primary-color hover:text-white transition-all duration-300"
                        disabled={isLoading}
                        title="View"
                      >
                        <FaEye />
                      </button>
                      <button
                        onClick={() => handleEdit(medicine)}
                        className="size-7 rounded-full bg-M-primary-color/20 text-M-primary-color text-sm inline-flex items-center justify-center hover:bg-M-primary-color hover:text-white transition-all duration-300"
                        disabled={isLoading}
                        title="Edit"
                      >
                        <FaEdit />
                      </button>
                      <button
                        onClick={() => handleOpenBadStockModal(medicine)}
                       className="size-7 rounded-full bg-M-secondary-color/10 text-M-secondary-color text-sm inline-flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300"
                        disabled={isLoading}
                        title="Bad Stock"
                      >
                        <FaMinus />
                      </button>
                      <button
                        onClick={() => handleSingleDelete(medicine.id)}
                        className="size-7 rounded-full bg-M-secondary-color/10 text-M-secondary-color text-sm inline-flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300"
                        disabled={isLoading}
                        title="Delete"
                      >
                        <RiDeleteBin6Line />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="print-hidden">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalRecords={totalRecords}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Medicines;